/**
 * Patient-history reference lists — medical history, medications, allergies.
 *
 * Consistent-phrasing conventions (enforced across every list):
 *   - Title Case condition/drug names
 *   - Generic name first, brand name in parens where relevant
 *     ("Apixaban (Eliquis)")
 *   - Abbreviations in parens at first mention ("Congestive Heart Failure (CHF)")
 *   - No trailing punctuation
 *   - Categorized into ClinicalGroup shape so the SearchableSelect can
 *     render collapsible sections for readability on long lists
 *
 * Users may always free-text anything off-list via the SearchableSelect's
 * "Add …" sentinel row.
 */

import type { ClinicalFlag, ClinicalGroup, ClinicalOption } from './clinical-dictionaries';

// ============================================================
// MEDICAL HISTORY
// ============================================================
export const MEDICAL_HISTORY: ClinicalGroup[] = [
  {
    category: 'Cardiovascular',
    options: [
      { label: 'Hypertension (HTN)' },
      { label: 'Coronary Artery Disease (CAD)' },
      { label: 'Myocardial Infarction (Prior)' },
      { label: 'Congestive Heart Failure (CHF)' },
      { label: 'Atrial Fibrillation' },
      { label: 'Atrial Flutter' },
      { label: 'Cardiomyopathy' },
      { label: 'Valvular Heart Disease' },
      { label: 'Aortic Stenosis' },
      { label: 'Aortic Regurgitation' },
      { label: 'Mitral Regurgitation' },
      { label: 'Peripheral Vascular Disease (PVD)' },
      { label: 'Peripheral Artery Disease (PAD)' },
      { label: 'Aortic Aneurysm' },
      { label: 'Pulmonary Embolism (Prior)' },
      { label: 'Deep Vein Thrombosis (Prior)' },
      { label: 'Hyperlipidemia' },
      { label: 'Pacemaker' },
      { label: 'Implantable Cardioverter-Defibrillator (ICD)' },
      { label: 'CABG (Prior)' },
      { label: 'Coronary Stent (Prior)' },
      { label: 'Left Ventricular Assist Device (LVAD)' },
      { label: 'Heart Transplant' },
    ],
  },
  {
    category: 'Pulmonary',
    options: [
      { label: 'Asthma' },
      { label: 'Chronic Obstructive Pulmonary Disease (COPD)' },
      { label: 'Emphysema' },
      { label: 'Chronic Bronchitis' },
      { label: 'Pulmonary Fibrosis' },
      { label: 'Obstructive Sleep Apnea (OSA)' },
      { label: 'Home Oxygen Use' },
      { label: 'CPAP / BiPAP at Home' },
      { label: 'Pneumonia (Recent)' },
      { label: 'Tuberculosis (History)' },
      { label: 'Cystic Fibrosis' },
      { label: 'Pulmonary Hypertension' },
      { label: 'Lung Transplant' },
    ],
  },
  {
    category: 'Neurological',
    options: [
      { label: 'Stroke / CVA (Prior)' },
      { label: 'Transient Ischemic Attack (TIA)' },
      { label: 'Seizure Disorder' },
      { label: 'Epilepsy' },
      { label: 'Parkinson Disease' },
      { label: 'Alzheimer Disease' },
      { label: 'Dementia' },
      { label: 'Migraine' },
      { label: 'Multiple Sclerosis (MS)' },
      { label: 'Traumatic Brain Injury (Prior)' },
      { label: 'Spinal Cord Injury' },
      { label: 'Guillain-Barré Syndrome (History)' },
      { label: 'ALS / Motor Neuron Disease' },
      { label: 'Myasthenia Gravis' },
      { label: 'Hydrocephalus / VP Shunt' },
    ],
  },
  {
    category: 'Endocrine',
    options: [
      { label: 'Diabetes Mellitus — Type 1' },
      { label: 'Diabetes Mellitus — Type 2' },
      { label: 'Gestational Diabetes (History)' },
      { label: 'Hypothyroidism' },
      { label: 'Hyperthyroidism' },
      { label: 'Adrenal Insufficiency' },
      { label: 'Addison Disease' },
      { label: 'Cushing Syndrome' },
      { label: 'Pituitary Disorder' },
    ],
  },
  {
    category: 'Renal / Genitourinary',
    options: [
      { label: 'Chronic Kidney Disease (CKD)' },
      { label: 'End-Stage Renal Disease (Dialysis Dependent)' },
      { label: 'Kidney Transplant' },
      { label: 'Kidney Stones (History)' },
      { label: 'Benign Prostatic Hyperplasia (BPH)' },
      { label: 'Recurrent UTIs' },
      { label: 'Neurogenic Bladder' },
    ],
  },
  {
    category: 'GI / Liver',
    options: [
      { label: 'GERD' },
      { label: 'Peptic Ulcer Disease (PUD)' },
      { label: 'Inflammatory Bowel Disease (IBD)' },
      { label: 'Crohn Disease' },
      { label: 'Ulcerative Colitis' },
      { label: 'Cirrhosis' },
      { label: 'Hepatitis B' },
      { label: 'Hepatitis C' },
      { label: 'Fatty Liver Disease (NAFLD)' },
      { label: 'Pancreatitis (History)' },
      { label: 'Gastric Bypass / Bariatric Surgery' },
      { label: 'Diverticulitis (History)' },
      { label: 'Celiac Disease' },
      { label: 'Liver Transplant' },
    ],
  },
  {
    category: 'Oncology',
    options: [
      { label: 'Cancer — Active Treatment' },
      { label: 'Cancer — History / Remission' },
      { label: 'Breast Cancer' },
      { label: 'Lung Cancer' },
      { label: 'Prostate Cancer' },
      { label: 'Colon Cancer' },
      { label: 'Pancreatic Cancer' },
      { label: 'Brain Tumor / Glioma' },
      { label: 'Leukemia' },
      { label: 'Lymphoma' },
      { label: 'Melanoma' },
      { label: 'Multiple Myeloma' },
      { label: 'Metastatic Disease' },
    ],
  },
  {
    category: 'Hematologic / Immune',
    options: [
      { label: 'Anemia' },
      { label: 'Iron Deficiency Anemia' },
      { label: 'Sickle Cell Disease' },
      { label: 'Hemophilia' },
      { label: 'Von Willebrand Disease' },
      { label: 'HIV / AIDS' },
      { label: 'Immunocompromised' },
      { label: 'Systemic Lupus Erythematosus (SLE)' },
      { label: 'Rheumatoid Arthritis (RA)' },
      { label: 'Scleroderma' },
      { label: 'Sjögren Syndrome' },
      { label: 'Organ Transplant Recipient' },
    ],
  },
  {
    category: 'Psychiatric',
    options: [
      { label: 'Depression' },
      { label: 'Anxiety Disorder' },
      { label: 'Panic Disorder' },
      { label: 'Bipolar Disorder' },
      { label: 'Schizophrenia' },
      { label: 'Schizoaffective Disorder' },
      { label: 'Post-Traumatic Stress Disorder (PTSD)' },
      { label: 'Attention Deficit / Hyperactivity Disorder (ADHD)' },
      { label: 'Autism Spectrum Disorder' },
      { label: 'Eating Disorder' },
      { label: 'Substance Use Disorder' },
      { label: 'Alcohol Use Disorder' },
      { label: 'Suicide Attempt (History)' },
    ],
  },
  {
    category: 'Musculoskeletal',
    options: [
      { label: 'Osteoarthritis' },
      { label: 'Osteoporosis' },
      { label: 'Chronic Back Pain' },
      { label: 'Chronic Pain Syndrome' },
      { label: 'Fibromyalgia' },
      { label: 'Gout' },
      { label: 'Herniated Disc' },
    ],
  },
  {
    category: 'OB/GYN',
    options: [
      { label: 'Currently Pregnant' },
      { label: 'Postpartum' },
      { label: 'Pre-Eclampsia (History)' },
      { label: 'Endometriosis' },
      { label: 'Polycystic Ovary Syndrome (PCOS)' },
      { label: 'Recent C-Section' },
    ],
  },
  {
    category: 'Special Needs / Devices',
    options: [
      { label: 'G-Tube / PEG' },
      { label: 'J-Tube' },
      { label: 'Tracheostomy' },
      { label: 'Home Ventilator' },
      { label: 'Central Line / Port' },
      { label: 'Ostomy (Colostomy / Ileostomy)' },
      { label: 'Indwelling Urinary Catheter' },
      { label: 'Baclofen Pump' },
      { label: 'Deep Brain Stimulator' },
      { label: 'Cochlear Implant' },
    ],
  },
  {
    category: 'End-of-Life / Care Status',
    options: [
      { label: 'Hospice / Comfort Care' },
      { label: 'DNR / MOLST / POLST' },
      { label: 'Advance Directive on File' },
      { label: 'Frequent Faller' },
    ],
  },
  {
    category: 'Other',
    options: [
      { label: 'No Known Medical History' },
      { label: 'Unknown' },
      { label: 'Other (Document in Narrative)' },
    ],
  },
];

// ============================================================
// MEDICATIONS
// ============================================================
export const MEDICATIONS: ClinicalGroup[] = [
  {
    category: 'Antihypertensives — ACE Inhibitors / ARBs',
    options: [
      { label: 'Lisinopril' },
      { label: 'Enalapril' },
      { label: 'Ramipril' },
      { label: 'Benazepril' },
      { label: 'Quinapril' },
      { label: 'Captopril' },
      { label: 'Losartan' },
      { label: 'Valsartan' },
      { label: 'Olmesartan' },
      { label: 'Irbesartan' },
      { label: 'Candesartan' },
      { label: 'Telmisartan' },
    ],
  },
  {
    category: 'Antihypertensives — Beta Blockers',
    options: [
      // Beta-blockers mask tachycardic response to shock/hypoglycemia —
      // tag so decision-support can prompt the provider.
      { label: 'Atenolol',                          flags: ['beta_blocker'] },
      { label: 'Metoprolol Tartrate (Lopressor)',   flags: ['beta_blocker'] },
      { label: 'Metoprolol Succinate (Toprol XL)',  flags: ['beta_blocker'] },
      { label: 'Carvedilol (Coreg)',                flags: ['beta_blocker'] },
      { label: 'Bisoprolol',                        flags: ['beta_blocker'] },
      { label: 'Propranolol',                       flags: ['beta_blocker', 'qt_prolonging'] },
      { label: 'Labetalol',                         flags: ['beta_blocker'] },
      { label: 'Nebivolol (Bystolic)',              flags: ['beta_blocker'] },
    ],
  },
  {
    category: 'Antihypertensives — Calcium Channel Blockers',
    options: [
      { label: 'Amlodipine (Norvasc)' },
      { label: 'Nifedipine' },
      { label: 'Diltiazem' },
      { label: 'Verapamil' },
      { label: 'Felodipine' },
    ],
  },
  {
    category: 'Antihypertensives — Diuretics / Other',
    options: [
      { label: 'Hydrochlorothiazide (HCTZ)' },
      { label: 'Chlorthalidone' },
      { label: 'Furosemide (Lasix)' },
      { label: 'Bumetanide (Bumex)' },
      { label: 'Torsemide' },
      { label: 'Spironolactone (Aldactone)' },
      { label: 'Eplerenone' },
      { label: 'Clonidine' },
      { label: 'Hydralazine' },
      { label: 'Minoxidil' },
    ],
  },
  {
    category: 'Cardiac — Antianginals / Nitrates',
    options: [
      { label: 'Nitroglycerin (Sublingual)' },
      { label: 'Nitroglycerin Patch' },
      { label: 'Isosorbide Mononitrate' },
      { label: 'Isosorbide Dinitrate' },
      { label: 'Ranolazine (Ranexa)' },
    ],
  },
  {
    category: 'Antiplatelets / Anticoagulants',
    options: [
      // Aspirin is intentionally NOT flagged antiplatelet — it's
      // ubiquitous enough that flagging it would become noise. All the
      // "serious" antiplatelets and every anticoagulant below carry
      // flags so the patient-data tab surfaces a prominent red banner.
      { label: 'Aspirin' },
      { label: 'Clopidogrel (Plavix)',      flags: ['antiplatelet'] },
      { label: 'Ticagrelor (Brilinta)',     flags: ['antiplatelet'] },
      { label: 'Prasugrel (Effient)',       flags: ['antiplatelet'] },
      // Dual antiplatelet therapy combos
      { label: 'Aspirin / Dipyridamole (Aggrenox)', flags: ['antiplatelet'] },
      { label: 'Dipyridamole',              flags: ['antiplatelet'] },
      { label: 'Cilostazol',                flags: ['antiplatelet'] },
      { label: 'Ticlopidine',               flags: ['antiplatelet'] },

      { label: 'Warfarin (Coumadin)',       flags: ['anticoagulant'] },
      { label: 'Apixaban (Eliquis)',        flags: ['anticoagulant'] },
      { label: 'Rivaroxaban (Xarelto)',     flags: ['anticoagulant'] },
      { label: 'Dabigatran (Pradaxa)',      flags: ['anticoagulant'] },
      { label: 'Edoxaban (Savaysa)',        flags: ['anticoagulant'] },
      { label: 'Enoxaparin (Lovenox)',      flags: ['anticoagulant'] },
      { label: 'Heparin',                   flags: ['anticoagulant'] },
      { label: 'Dalteparin (Fragmin)',      flags: ['anticoagulant'] },
      { label: 'Fondaparinux (Arixtra)',    flags: ['anticoagulant'] },
      { label: 'Bivalirudin (Angiomax)',    flags: ['anticoagulant'] },
      { label: 'Argatroban',                flags: ['anticoagulant'] },
    ],
  },
  {
    category: 'Lipid-Lowering',
    options: [
      { label: 'Atorvastatin (Lipitor)' },
      { label: 'Simvastatin (Zocor)' },
      { label: 'Rosuvastatin (Crestor)' },
      { label: 'Pravastatin' },
      { label: 'Lovastatin' },
      { label: 'Ezetimibe (Zetia)' },
      { label: 'Gemfibrozil' },
      { label: 'Fenofibrate' },
      { label: 'Evolocumab (Repatha)' },
    ],
  },
  {
    category: 'Diabetes — Oral',
    options: [
      { label: 'Metformin' },
      { label: 'Glipizide' },
      { label: 'Glyburide' },
      { label: 'Glimepiride' },
      { label: 'Pioglitazone (Actos)' },
      { label: 'Sitagliptin (Januvia)' },
      { label: 'Linagliptin (Tradjenta)' },
      { label: 'Empagliflozin (Jardiance)' },
      { label: 'Dapagliflozin (Farxiga)' },
      { label: 'Canagliflozin (Invokana)' },
    ],
  },
  {
    category: 'Diabetes — Injectable / GLP-1',
    options: [
      { label: 'Semaglutide (Ozempic)' },
      { label: 'Semaglutide (Wegovy)' },
      { label: 'Liraglutide (Victoza)' },
      { label: 'Dulaglutide (Trulicity)' },
      { label: 'Tirzepatide (Mounjaro)' },
      { label: 'Exenatide (Byetta / Bydureon)' },
    ],
  },
  {
    category: 'Diabetes — Insulins',
    // Insulin flag drives a glucose-check prompt on the patient-data
    // tab (hypoglycemia has to be ruled out on any ALOC patient on
    // insulin therapy).
    options: [
      { label: 'Insulin Glargine (Lantus)',   flags: ['insulin'] },
      { label: 'Insulin Glargine (Basaglar)', flags: ['insulin'] },
      { label: 'Insulin Glargine (Toujeo)',   flags: ['insulin'] },
      { label: 'Insulin Detemir (Levemir)',   flags: ['insulin'] },
      { label: 'Insulin Degludec (Tresiba)',  flags: ['insulin'] },
      { label: 'Insulin NPH',                 flags: ['insulin'] },
      { label: 'Regular Insulin (Humulin R)', flags: ['insulin'] },
      { label: 'Insulin Aspart (Novolog)',    flags: ['insulin'] },
      { label: 'Insulin Lispro (Humalog)',    flags: ['insulin'] },
      { label: 'Insulin Glulisine (Apidra)',  flags: ['insulin'] },
      { label: 'Insulin Pump',                flags: ['insulin'] },
    ],
  },
  {
    category: 'Pulmonary — Inhalers',
    options: [
      { label: 'Albuterol (ProAir / Ventolin)' },
      { label: 'Levalbuterol (Xopenex)' },
      { label: 'Ipratropium (Atrovent)' },
      { label: 'Ipratropium / Albuterol (Combivent / DuoNeb)' },
      { label: 'Tiotropium (Spiriva)' },
      { label: 'Umeclidinium (Incruse)' },
      { label: 'Fluticasone (Flovent)' },
      { label: 'Fluticasone / Salmeterol (Advair)' },
      { label: 'Budesonide (Pulmicort)' },
      { label: 'Budesonide / Formoterol (Symbicort)' },
      { label: 'Mometasone / Formoterol (Dulera)' },
      { label: 'Vilanterol / Fluticasone (Breo)' },
    ],
  },
  {
    category: 'Pulmonary — Oral / Other',
    options: [
      { label: 'Montelukast (Singulair)' },
      { label: 'Theophylline' },
      { label: 'Prednisone' },
    ],
  },
  {
    category: 'Antiepileptics / Neuro',
    options: [
      { label: 'Levetiracetam (Keppra)' },
      { label: 'Phenytoin (Dilantin)' },
      { label: 'Lamotrigine (Lamictal)' },
      { label: 'Carbamazepine (Tegretol)' },
      { label: 'Oxcarbazepine (Trileptal)' },
      { label: 'Valproic Acid / Depakote' },
      { label: 'Gabapentin (Neurontin)' },
      { label: 'Pregabalin (Lyrica)' },
      { label: 'Topiramate (Topamax)' },
      { label: 'Zonisamide (Zonegran)' },
      { label: 'Lacosamide (Vimpat)' },
      { label: 'Levodopa / Carbidopa (Sinemet)' },
      { label: 'Pramipexole (Mirapex)' },
      { label: 'Ropinirole (Requip)' },
      { label: 'Donepezil (Aricept)' },
      { label: 'Memantine (Namenda)' },
    ],
  },
  {
    category: 'Psychiatric — SSRIs / SNRIs',
    options: [
      { label: 'Sertraline (Zoloft)' },
      { label: 'Fluoxetine (Prozac)' },
      { label: 'Escitalopram (Lexapro)' },
      { label: 'Citalopram (Celexa)' },
      { label: 'Paroxetine (Paxil)' },
      { label: 'Venlafaxine (Effexor)' },
      { label: 'Duloxetine (Cymbalta)' },
      { label: 'Desvenlafaxine (Pristiq)' },
    ],
  },
  {
    category: 'Psychiatric — Other',
    options: [
      { label: 'Bupropion (Wellbutrin)' },
      { label: 'Mirtazapine (Remeron)' },
      { label: 'Trazodone' },
      { label: 'Amitriptyline' },
      { label: 'Nortriptyline' },
      { label: 'Alprazolam (Xanax)',   flags: ['benzodiazepine'] },
      { label: 'Lorazepam (Ativan)',   flags: ['benzodiazepine'] },
      { label: 'Clonazepam (Klonopin)',flags: ['benzodiazepine'] },
      { label: 'Diazepam (Valium)',    flags: ['benzodiazepine'] },
      { label: 'Temazepam (Restoril)', flags: ['benzodiazepine'] },
      { label: 'Chlordiazepoxide (Librium)', flags: ['benzodiazepine'] },
      { label: 'Midazolam (Versed)',   flags: ['benzodiazepine'] },
      { label: 'Zolpidem (Ambien)' },
      { label: 'Eszopiclone (Lunesta)' },
      { label: 'Buspirone (Buspar)' },
    ],
  },
  {
    category: 'Psychiatric — Antipsychotics / Mood',
    options: [
      { label: 'Quetiapine (Seroquel)' },
      { label: 'Risperidone (Risperdal)' },
      { label: 'Olanzapine (Zyprexa)' },
      { label: 'Aripiprazole (Abilify)' },
      { label: 'Haloperidol (Haldol)' },
      { label: 'Ziprasidone (Geodon)' },
      { label: 'Clozapine (Clozaril)' },
      { label: 'Lithium' },
      { label: 'Methylphenidate (Ritalin / Concerta)' },
      { label: 'Amphetamine / Dextroamphetamine (Adderall)' },
      { label: 'Lisdexamfetamine (Vyvanse)' },
      { label: 'Atomoxetine (Strattera)' },
    ],
  },
  {
    category: 'Pain — Non-Opioid',
    options: [
      { label: 'Acetaminophen (Tylenol)' },
      { label: 'Ibuprofen (Motrin / Advil)' },
      { label: 'Naproxen (Aleve)' },
      { label: 'Celecoxib (Celebrex)' },
      { label: 'Meloxicam (Mobic)' },
      { label: 'Diclofenac' },
      { label: 'Ketorolac (Toradol)' },
      { label: 'Cyclobenzaprine (Flexeril)' },
      { label: 'Tizanidine (Zanaflex)' },
      { label: 'Baclofen' },
    ],
  },
  {
    category: 'Pain — Opioids',
    options: [
      { label: 'Tramadol',                                      flags: ['opioid'] },
      { label: 'Hydrocodone / Acetaminophen (Norco / Vicodin)', flags: ['opioid'] },
      { label: 'Oxycodone',                                     flags: ['opioid', 'controlled_2'] },
      { label: 'Oxycodone / Acetaminophen (Percocet)',          flags: ['opioid', 'controlled_2'] },
      { label: 'Morphine (Oral / IR)',                          flags: ['opioid', 'controlled_2'] },
      { label: 'Morphine (Sustained Release / MS Contin)',      flags: ['opioid', 'controlled_2'] },
      { label: 'Fentanyl Patch',                                flags: ['opioid', 'controlled_2'] },
      { label: 'Methadone',                                     flags: ['opioid', 'qt_prolonging'] },
      { label: 'Buprenorphine (Suboxone)',                      flags: ['opioid'] },
      { label: 'Buprenorphine Patch (Butrans)',                 flags: ['opioid'] },
      { label: 'Naltrexone' },
      { label: 'Hydromorphone (Dilaudid)',                      flags: ['opioid', 'controlled_2'] },
      { label: 'Oxymorphone (Opana)',                           flags: ['opioid', 'controlled_2'] },
      { label: 'Codeine',                                       flags: ['opioid'] },
    ],
  },
  {
    category: 'GI',
    options: [
      { label: 'Omeprazole (Prilosec)' },
      { label: 'Pantoprazole (Protonix)' },
      { label: 'Esomeprazole (Nexium)' },
      { label: 'Lansoprazole (Prevacid)' },
      { label: 'Famotidine (Pepcid)' },
      { label: 'Ranitidine (Zantac)' },
      { label: 'Sucralfate (Carafate)' },
      { label: 'Ondansetron (Zofran)' },
      { label: 'Promethazine (Phenergan)' },
      { label: 'Metoclopramide (Reglan)' },
      { label: 'Dicyclomine (Bentyl)' },
      { label: 'Loperamide (Imodium)' },
      { label: 'Polyethylene Glycol (MiraLAX)' },
      { label: 'Docusate (Colace)' },
    ],
  },
  {
    category: 'Thyroid / Endocrine',
    options: [
      { label: 'Levothyroxine (Synthroid)' },
      { label: 'Liothyronine (Cytomel)' },
      { label: 'Methimazole' },
      { label: 'Propylthiouracil (PTU)' },
      { label: 'Hydrocortisone' },
      { label: 'Fludrocortisone' },
    ],
  },
  {
    category: 'Steroids / Immunologic',
    options: [
      // Chronic immunosuppression → sepsis / infection risk prompts
      { label: 'Prednisone' },
      { label: 'Methylprednisolone (Medrol)' },
      { label: 'Dexamethasone' },
      { label: 'Hydrocortisone' },
      { label: 'Methotrexate',                  flags: ['immunosuppressant'] },
      { label: 'Hydroxychloroquine (Plaquenil)' },
      { label: 'Adalimumab (Humira)',           flags: ['immunosuppressant'] },
      { label: 'Etanercept (Enbrel)',           flags: ['immunosuppressant'] },
      { label: 'Infliximab (Remicade)',         flags: ['immunosuppressant'] },
      { label: 'Ustekinumab (Stelara)',         flags: ['immunosuppressant'] },
      { label: 'Rituximab',                     flags: ['immunosuppressant'] },
      { label: 'Tacrolimus',                    flags: ['immunosuppressant'] },
      { label: 'Cyclosporine',                  flags: ['immunosuppressant'] },
      { label: 'Mycophenolate (CellCept)',      flags: ['immunosuppressant'] },
      { label: 'Azathioprine (Imuran)',         flags: ['immunosuppressant'] },
    ],
  },
  {
    category: 'Renal / Electrolytes',
    options: [
      { label: 'Potassium Chloride' },
      { label: 'Calcium Carbonate (TUMS)' },
      { label: 'Sodium Bicarbonate' },
      { label: 'Sevelamer (Renvela)' },
      { label: 'Calcitriol' },
      { label: 'Erythropoietin (Epogen / Procrit)' },
      { label: 'Darbepoetin (Aranesp)' },
    ],
  },
  {
    category: 'Antibiotics',
    options: [
      { label: 'Amoxicillin' },
      { label: 'Amoxicillin / Clavulanate (Augmentin)' },
      { label: 'Azithromycin (Zithromax)' },
      { label: 'Clarithromycin' },
      { label: 'Ciprofloxacin' },
      { label: 'Levofloxacin' },
      { label: 'Doxycycline' },
      { label: 'Cephalexin (Keflex)' },
      { label: 'Cefdinir (Omnicef)' },
      { label: 'Sulfamethoxazole / Trimethoprim (Bactrim)' },
      { label: 'Metronidazole (Flagyl)' },
      { label: 'Clindamycin' },
      { label: 'Nitrofurantoin (Macrobid)' },
      { label: 'Vancomycin' },
      { label: 'Linezolid (Zyvox)' },
    ],
  },
  {
    category: 'GU / ED',
    options: [
      { label: 'Tamsulosin (Flomax)' },
      { label: 'Finasteride (Proscar / Propecia)' },
      { label: 'Sildenafil (Viagra)' },
      { label: 'Tadalafil (Cialis)' },
      { label: 'Vardenafil (Levitra)' },
    ],
  },
  {
    category: 'Reversal / Antidotes',
    options: [
      { label: 'Idarucizumab (Praxbind)' },
      { label: 'Andexanet Alfa (Andexxa)' },
      { label: 'Protamine Sulfate' },
      { label: 'Vitamin K (Phytonadione)' },
      { label: 'Naloxone (Narcan)' },
      { label: 'Flumazenil (Romazicon)' },
    ],
  },
  {
    category: 'Other',
    options: [
      { label: 'Takes No Medications' },
      { label: 'Unknown / Patient Unable to Report' },
      { label: 'Other (Document in Narrative)' },
    ],
  },
];

// ============================================================
// ALLERGIES
// ============================================================
export const ALLERGIES: ClinicalGroup[] = [
  ...drugAllergyGroups(),
  ...environmentalAllergyGroups(),
];

/**
 * Drug allergies — medications, medication classes, contrast agents,
 * and vaccines. Comprehensive enough that the crew can
 * find the vast majority of patient-reported drug allergies from the
 * searchable dropdown without free-text entry.
 */
export const DRUG_ALLERGIES: ClinicalGroup[] = drugAllergyGroups();

/**
 * Environmental allergies — foods, stings/bites, airborne allergens,
 * animal dander, adhesives, and other non-medication allergens.
 */
export const ENVIRONMENTAL_ALLERGIES: ClinicalGroup[] = environmentalAllergyGroups();

function drugAllergyGroups(): ClinicalGroup[] {
  return [
    {
      category: 'Antibiotics',
      options: [
        { label: 'Penicillin' },
        { label: 'Amoxicillin' },
        { label: 'Ampicillin' },
        { label: 'Cephalosporins (All)' },
        { label: 'Cephalexin (Keflex)' },
        { label: 'Cefazolin (Ancef)' },
        { label: 'Ceftriaxone (Rocephin)' },
        { label: 'Sulfa / Sulfonamides' },
        { label: 'Sulfamethoxazole / Trimethoprim (Bactrim)' },
        { label: 'Tetracycline' },
        { label: 'Doxycycline' },
        { label: 'Minocycline' },
        { label: 'Erythromycin' },
        { label: 'Azithromycin (Z-Pak)' },
        { label: 'Clarithromycin' },
        { label: 'Clindamycin' },
        { label: 'Ciprofloxacin' },
        { label: 'Levofloxacin' },
        { label: 'Moxifloxacin' },
        { label: 'Vancomycin' },
        { label: 'Metronidazole (Flagyl)' },
        { label: 'Nitrofurantoin (Macrobid)' },
        { label: 'Linezolid' },
      ],
    },
    {
      category: 'NSAIDs / Analgesics',
      options: [
        { label: 'Aspirin' },
        { label: 'Ibuprofen (Motrin / Advil)' },
        { label: 'Naproxen (Aleve)' },
        { label: 'Diclofenac' },
        { label: 'Meloxicam' },
        { label: 'Celecoxib (Celebrex)' },
        { label: 'NSAIDs (All / Class)' },
        { label: 'Acetaminophen (Tylenol)' },
        { label: 'Ketorolac (Toradol)' },
      ],
    },
    {
      category: 'Opioids',
      options: [
        { label: 'Morphine' },
        { label: 'Codeine' },
        { label: 'Oxycodone (Percocet / OxyContin)' },
        { label: 'Hydrocodone (Vicodin / Norco)' },
        { label: 'Fentanyl' },
        { label: 'Meperidine (Demerol)' },
        { label: 'Hydromorphone (Dilaudid)' },
        { label: 'Tramadol (Ultram)' },
        { label: 'Buprenorphine (Suboxone)' },
        { label: 'Methadone' },
        { label: 'Opioids (All / Class)' },
      ],
    },
    {
      category: 'Local / General Anesthetics',
      options: [
        { label: 'Lidocaine' },
        { label: 'Bupivacaine (Marcaine)' },
        { label: 'Procaine (Novocaine)' },
        { label: 'Benzocaine' },
        { label: 'Propofol' },
        { label: 'Ketamine' },
        { label: 'Succinylcholine' },
        { label: 'Etomidate' },
      ],
    },
    {
      category: 'Cardiac / Anticoagulant',
      options: [
        { label: 'ACE Inhibitors (All)' },
        { label: 'Lisinopril' },
        { label: 'ARBs (All)' },
        { label: 'Losartan' },
        { label: 'Statins (All)' },
        { label: 'Atorvastatin (Lipitor)' },
        { label: 'Simvastatin' },
        { label: 'Beta Blockers (All)' },
        { label: 'Metoprolol' },
        { label: 'Atenolol' },
        { label: 'Heparin' },
        { label: 'Low-Molecular-Weight Heparin (Lovenox)' },
        { label: 'Warfarin (Coumadin)' },
        { label: 'Apixaban (Eliquis)' },
        { label: 'Rivaroxaban (Xarelto)' },
        { label: 'Dabigatran (Pradaxa)' },
        { label: 'Clopidogrel (Plavix)' },
      ],
    },
    {
      category: 'Psychiatric / Neuro',
      options: [
        { label: 'SSRIs (All)' },
        { label: 'Sertraline (Zoloft)' },
        { label: 'Fluoxetine (Prozac)' },
        { label: 'Benzodiazepines (All)' },
        { label: 'Diazepam (Valium)' },
        { label: 'Lorazepam (Ativan)' },
        { label: 'Midazolam (Versed)' },
        { label: 'Haloperidol (Haldol)' },
        { label: 'Olanzapine (Zyprexa)' },
        { label: 'Phenytoin (Dilantin)' },
        { label: 'Carbamazepine (Tegretol)' },
        { label: 'Lamotrigine (Lamictal)' },
        { label: 'Lithium' },
      ],
    },
    {
      category: 'Contrast / Imaging',
      options: [
        { label: 'Iodine / Iodinated Contrast' },
        { label: 'Gadolinium Contrast (MRI)' },
        { label: 'Shellfish-Related Iodine Concern' },
      ],
    },
    {
      category: 'Vaccines',
      options: [
        { label: 'Influenza Vaccine' },
        { label: 'COVID-19 Vaccine (mRNA)' },
        { label: 'COVID-19 Vaccine (J&J)' },
        { label: 'Pneumococcal Vaccine (Prevnar / Pneumovax)' },
        { label: 'Tetanus / Td / Tdap' },
        { label: 'MMR Vaccine' },
        { label: 'Shingrix (Shingles)' },
        { label: 'Egg (Vaccine Component)' },
      ],
    },
    {
      category: 'Other Drugs',
      options: [
        { label: 'Metformin' },
        { label: 'Insulin' },
        { label: 'Corticosteroids' },
        { label: 'Prednisone' },
        { label: 'Epinephrine' },
        { label: 'Nitroglycerin' },
        { label: 'Albuterol' },
        { label: 'Unknown Drug' },
        { label: 'Other Drug (Document in Narrative)' },
      ],
    },
  ];
}

function environmentalAllergyGroups(): ClinicalGroup[] {
  return [
    {
      category: 'Foods — Tree Nuts / Peanuts',
      options: [
        { label: 'Peanuts' },
        { label: 'Tree Nuts (All)' },
        { label: 'Almonds' },
        { label: 'Cashews' },
        { label: 'Walnuts' },
        { label: 'Pecans' },
        { label: 'Pistachios' },
        { label: 'Hazelnuts' },
        { label: 'Brazil Nuts' },
        { label: 'Macadamia Nuts' },
        { label: 'Coconut' },
      ],
    },
    {
      category: 'Foods — Seafood',
      options: [
        { label: 'Shellfish (All)' },
        { label: 'Shrimp' },
        { label: 'Crab' },
        { label: 'Lobster' },
        { label: 'Clams / Mussels / Oysters' },
        { label: 'Scallops' },
        { label: 'Fish (All)' },
        { label: 'Tuna' },
        { label: 'Salmon' },
      ],
    },
    {
      category: 'Foods — Other',
      options: [
        { label: 'Eggs' },
        { label: 'Milk / Dairy' },
        { label: 'Soy' },
        { label: 'Wheat / Gluten' },
        { label: 'Sesame' },
        { label: 'Strawberries' },
        { label: 'Chocolate' },
        { label: 'Mustard' },
        { label: 'Corn' },
        { label: 'Garlic' },
        { label: 'MSG' },
        { label: 'Food Dyes (Red #40 / Yellow #5)' },
        { label: 'Sulfites' },
      ],
    },
    {
      category: 'Stings / Bites',
      options: [
        { label: 'Bee Sting' },
        { label: 'Wasp Sting' },
        { label: 'Hornet Sting' },
        { label: 'Yellow Jacket' },
        { label: 'Fire Ant' },
        { label: 'Mosquito' },
        { label: 'Tick Bite' },
        { label: 'Spider Bite' },
        { label: 'Snake Bite' },
        { label: 'Jellyfish' },
      ],
    },
    {
      category: 'Airborne / Seasonal',
      options: [
        { label: 'Pollen (General)' },
        { label: 'Tree Pollen' },
        { label: 'Grass Pollen' },
        { label: 'Ragweed' },
        { label: 'Mold / Mildew' },
        { label: 'Dust Mites' },
        { label: 'Dust (Household)' },
        { label: 'Smoke' },
        { label: 'Perfume / Fragrance' },
      ],
    },
    {
      category: 'Animal Dander',
      options: [
        { label: 'Cat Dander' },
        { label: 'Dog Dander' },
        { label: 'Horse Dander' },
        { label: 'Rodent Dander' },
        { label: 'Bird Feathers / Dander' },
      ],
    },
    {
      category: 'Adhesives / Materials',
      options: [
        { label: 'Latex' },
        { label: 'Tape / Adhesive' },
        { label: 'EKG Electrode Gel' },
        { label: 'Silicone' },
        { label: 'Nickel' },
        { label: 'Chlorhexidine' },
        { label: 'Povidone-Iodine (Betadine)' },
        { label: 'Wool' },
        { label: 'Cosmetics' },
        { label: 'Nail Polish / Acetone' },
      ],
    },
    {
      category: 'Chemical / Environmental',
      options: [
        { label: 'Cleaning Products (Bleach / Ammonia)' },
        { label: 'Pesticides' },
        { label: 'Paint Fumes' },
        { label: 'Formaldehyde' },
        { label: 'Poison Ivy / Oak / Sumac' },
      ],
    },
    {
      category: 'Other',
      options: [
        { label: 'Unknown Environmental Allergen' },
        { label: 'Other (Document in Narrative)' },
      ],
    },
  ];
}

// ============================================================
// Legacy flat-list exports
// ============================================================
// A handful of existing callers import MEDICAL_HISTORY_SUGGESTIONS /
// MEDICATION_SUGGESTIONS / ALLERGY_SUGGESTIONS as string arrays from
// before the grouped refactor. Preserve those as derived values so the
// migration can proceed without breaking the old chip list pathway.

function flattenLabels(groups: ClinicalGroup[]): string[] {
  return groups.flatMap((g) => g.options.map((o: ClinicalOption) => o.label));
}

export const MEDICAL_HISTORY_SUGGESTIONS: string[] = flattenLabels(MEDICAL_HISTORY);
export const MEDICATION_SUGGESTIONS: string[] = flattenLabels(MEDICATIONS);
export const ALLERGY_SUGGESTIONS: string[] = flattenLabels(ALLERGIES);

// ============================================================
// Lookup: medication label → clinical flags
// ============================================================
// Flat index so decision-support UI can check a free-text + selected
// medication against its tags in O(1). Free-text-added meds return no
// flags (expected — unknown meds are untagged).
const MED_FLAG_INDEX: Map<string, ClinicalFlag[]> = (() => {
  const m = new Map<string, ClinicalFlag[]>();
  for (const group of MEDICATIONS) {
    for (const opt of group.options) {
      if (opt.flags?.length) m.set(opt.label.toLowerCase(), opt.flags);
    }
  }
  return m;
})();

export function flagsForMedication(label: string): ClinicalFlag[] {
  return MED_FLAG_INDEX.get(label.toLowerCase()) ?? [];
}

/** True if ANY of the patient's listed meds carries `flag`. */
export function patientHasFlaggedMed(
  meds: Array<{ name: string; clinical_flags?: ClinicalFlag[] | null }>,
  flag: ClinicalFlag,
): boolean {
  return meds.some((m) => {
    // Prefer flags persisted on the record (survives dictionary updates);
    // fall back to the live lookup for older records that predated the flag system.
    if (m.clinical_flags?.includes(flag)) return true;
    return flagsForMedication(m.name).includes(flag);
  });
}
